<script>
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import TranscriptSummaryCard from '@modules/crm/components/transcript-summary-card/transcript-summary-card.vue'
import TranscriptCriteriaBars from '@modules/crm/components/transcript-criteria-bars/transcript-criteria-bars.vue'
import TranscriptScoreGauge from '@modules/crm/components/transcript-score-gauge/transcript-score-gauge.vue'
import TranscriptReasonAction from '@modules/crm/components/transcript-reason-action/transcript-reason-action.vue'
import TranscriptRecommendationBox from '@modules/crm/components/transcript-recommendation-box/transcript-recommendation-box.vue'
import {
  scoreColor,
  scoreColorSoft,
  scoreColorHex,
  formatScore,
  toScore,
} from '@modules/crm/utils/transcriptScore'

export default {
  name: 'DetailTabAvaliacao',
  components: {
    FeedbackWrapper,
    TranscriptSummaryCard,
    TranscriptCriteriaBars,
    TranscriptScoreGauge,
    TranscriptReasonAction,
    TranscriptRecommendationBox,
  },
  props: {
    transcript: { type: Object, default: null },
    scores: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  computed: {
    hasProject() {
      return !!(this.transcript && this.transcript.id_project)
    },
    aiStatus() {
      return (this.transcript && this.transcript.ai_status) || ''
    },
    projectScore() {
      return toScore(this.transcript && this.transcript.project_score)
    },
    // Motivo da nota geral (project_reason) — campo já presente no payload.
    projectReason() {
      return (this.transcript && this.transcript.project_reason) || ''
    },
    projectImprovement() {
      return (this.transcript && this.transcript.project_improvement_suggestion) || ''
    },
    hasScoreCard() {
      return this.projectScore !== null
    },
    // Lista normalizada por critério (nome, nota, cor, motivo, sugestão).
    criteria() {
      const rows = Array.isArray(this.scores) ? this.scores : []
      return rows.map((row) => this.normalizeCriterion(row))
    },
    // Critérios ordenados da menor nota para a maior, para dar consistência
    // visual entre o resumo (barras) e a avaliação detalhada. Notas ausentes
    // (null) vão para o fim. Usa cópia para não mutar `criteria`.
    sortedCriteria() {
      return this.criteria.slice().sort(this.compareByScoreAsc)
    },
    hasScores() {
      return this.criteria.length > 0
    },
    isProcessing() {
      return this.aiStatus === 'processing'
    },
    // Há conteúdo de avaliação a exibir (nota geral e/ou critérios) para um
    // projeto vinculado. Quando falso, o FeedbackWrapper mostra o estado vazio.
    hasContent() {
      return this.hasProject && (this.hasScores || this.projectScore !== null)
    },
    emptyMessage() {
      if (!this.hasProject) return 'Vincule um projeto para ver a avaliação.'
      if (this.isProcessing) return 'Processando avaliação…'
      return 'Avaliação ainda não disponível.'
    },
  },
  methods: {
    // Normaliza uma linha de score em { id, name, score, scoreLabel, barValue,
    // color, colorSoft, reason, improvement }. Extraído de `criteria` para manter
    // cada função com uma responsabilidade e baixa complexidade.
    normalizeCriterion(row) {
      // toScore (util compartilhado) normaliza; a nota por critério é exibida com
      // uma casa decimal (formatScore) para bater entre barras e chip.
      const score = toScore(row && row.score)
      return {
        id: this.criterionId(row),
        name: (row && row.name_criteria) || 'Critério',
        score,
        scoreLabel: formatScore(score),
        barValue: score === null ? 0 : score * 10,
        color: scoreColor(score),
        colorSoft: scoreColorSoft(score),
        colorHex: scoreColorHex(score),
        reason: this.joinList(row && row.score_reason),
        improvement: this.joinList(row && row.improvement_suggestion),
      }
    },
    // Comparador ascendente por nota (menor → maior); null por último.
    compareByScoreAsc(a, b) {
      if (a.score === null && b.score === null) return 0
      if (a.score === null) return 1
      if (b.score === null) return -1
      return a.score - b.score
    },
    // Identificador estável do critério (id real, id do critério ou o nome).
    criterionId(row) {
      if (!row) return ''
      return row.id || row.id_project_criteria || row.name_criteria || ''
    },
    // score_reason/improvement_suggestion vêm como array de strings; junta em
    // uma linha legível, ignorando entradas vazias. Aceita string solta também.
    joinList(value) {
      if (!value) return ''
      if (Array.isArray(value)) {
        return value
          .filter((item) => item !== null && item !== undefined && String(item).trim() !== '')
          .join(' ')
      }
      return String(value)
    },
  },
}
</script>

<template>
  <div class="detail-tab-avaliacao">
    <!-- FeedbackWrapper trata loading (skeleton) e estado vazio padronizados -->
    <FeedbackWrapper
      :loading="loading"
      :no-data="!loading && !hasContent"
      :message="emptyMessage"
      :min-height="160"
    >
      <!-- 1) Card de nota (TranscriptSummaryCard sem título): gauge da nota à
           esquerda + timeline (motivo / ação recomendada) à direita. -->
      <v-fade-transition>
        <TranscriptSummaryCard v-if="hasScoreCard">
          <div class="d-flex flex-column flex-md-row align-md-center">
            <div class="detail-tab-avaliacao__score-col d-flex justify-center">
              <TranscriptScoreGauge :score="projectScore" :size="96" />
            </div>

            <div class="flex-grow-1 mt-5 mt-md-0 ml-md-8">
              <TranscriptReasonAction
                :reason="projectReason"
                :recommendation="projectImprovement"
              />
            </div>
          </div>
        </TranscriptSummaryCard>
      </v-fade-transition>

      <!-- 2) Resumo dos critérios em barras (escala fixa 0–10), ordenado da
           menor nota para a maior. Título vem do TranscriptSummaryCard. -->
      <v-fade-transition>
        <TranscriptSummaryCard v-if="hasScores" title="Resumo dos critérios">
          <TranscriptCriteriaBars :criteria="sortedCriteria" />
        </TranscriptSummaryCard>
      </v-fade-transition>

      <!-- 3) Avaliação detalhada: um card branco por critério, mesma ordem do
           resumo (menor → maior), com borda esquerda na cor da nota. -->
      <v-fade-transition>
        <TranscriptSummaryCard v-if="hasScores" title="Avaliação detalhada">
          <div
            v-for="item in sortedCriteria"
            :key="item.id"
            class="detail-tab-avaliacao__criterion-card rounded-sm pa-3 mb-4"
            :style="{ borderLeftColor: item.color }"
          >
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="az-text-primary az-size-12 font-semibold">
                {{ item.name }}
              </span>
              <!-- Nota em v-chip: fundo na cor da nota suavizada + texto na cor da nota -->
              <v-chip
                small
                class="font-semibold"
                :style="{ color: item.color, backgroundColor: item.colorSoft }"
              >
                {{ item.scoreLabel }}/10
              </v-chip>
            </div>
            <p v-if="item.reason" class="az-text-primary az-size-14 mb-0">
              {{ item.reason }}
            </p>

            <!-- Caixa "Como melhorar": idêntica à "Ação recomendada" do card
                 da nota (mesmo TranscriptRecommendationBox). -->
            <TranscriptRecommendationBox
              v-if="item.improvement"
              title="Como melhorar"
              :text="item.improvement"
              class="mt-3"
            />
          </div>
        </TranscriptSummaryCard>
      </v-fade-transition>
    </FeedbackWrapper>
  </div>
</template>

<style lang="sass" scoped>
.detail-tab-avaliacao
  // Coluna do gauge: largura mínima (comprime a parte da nota).
  // Coluna do gauge com largura mínima maior que o anel (96px) → o
  // justify-center deixa o gauge centralizado (não colado na esquerda).
  &__score-col
    flex: 0 0 auto
    min-width: 150px

  // Card do critério: branco (contrasta com o flat-gray do container) + borda
  // fina e borda esquerda (4px) na cor da nota (aplicada inline via
  // borderLeftColor).
  &__criterion-card
    background: var(--white)
    border: 1px solid #F0F1F3
    border-left: 2px solid var(--gray-5)

  // Compacta os cards de seção (TranscriptSummaryCard) apenas nesta aba —
  // padding interno menor e título mais próximo do conteúdo, aproveitando
  // melhor o espaço do modal.
  ::v-deep .transcript-summary-card
    padding: 12px 16px

    .mb-5
      margin-bottom: 12px
</style>
